import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import {
  Battery,
  Thermometer,
  Zap,
  Activity,
  Gauge,
  RefreshCw,
  Pause,
  Play
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip
} from "recharts";

const LiveData = ({ connectionStatus }) => {
  const [liveData, setLiveData] = useState(null);
  const [isStreaming, setIsStreaming] = useState(true);
  const [powerHistory, setPowerHistory] = useState([]);
  const [tempHistory, setTempHistory] = useState([]);

  useEffect(() => {
    if (connectionStatus.connected && isStreaming) {
      fetchLiveData();
      const interval = setInterval(fetchLiveData, 1000);
      return () => clearInterval(interval);
    }
  }, [connectionStatus.connected, isStreaming]);

  const fetchLiveData = async () => {
    try {
      const response = await axios.get(`${API}/live-data/snapshot`);
      setLiveData(response.data);
      
      // Update history
      const timestamp = new Date().toLocaleTimeString();
      setPowerHistory(prev => [...prev.slice(-30), {
        time: timestamp,
        power: response.data.power_output,
        regen: response.data.regen_power
      }]);
      setTempHistory(prev => [...prev.slice(-30), {
        time: timestamp,
        battery: response.data.battery_temp,
        motor: response.data.motor_rear_temp,
        inverter: response.data.inverter_temp
      }]);
    } catch (error) {
      console.error("Failed to fetch live data:", error);
    }
  };

  const getCellColor = (voltage, baseVoltage = 3.7) => {
    const diff = voltage - baseVoltage;
    if (diff > 0.15) return "bg-[#30D158]";
    if (diff > 0.05) return "bg-[#30D158]/70";
    if (diff > -0.05) return "bg-[#007AFF]";
    if (diff > -0.15) return "bg-[#FF9F0A]";
    return "bg-[#FF3B30]";
  };

  const getTempColor = (temp) => {
    if (temp < 25) return "text-[#007AFF]";
    if (temp < 35) return "text-[#30D158]";
    if (temp < 45) return "text-[#FF9F0A]";
    return "text-[#FF3B30]";
  };

  // Gauge component
  const GaugeDisplay = ({ value, max, unit, label, color = "#007AFF" }) => {
    const percentage = Math.min((value / max) * 100, 100);
    const circumference = 2 * Math.PI * 60;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;
    
    return (
      <div className="gauge-container">
        <svg className="gauge-svg" width="160" height="160">
          <circle
            className="gauge-background"
            cx="80"
            cy="80"
            r="60"
          />
          <circle
            className="gauge-value"
            cx="80"
            cy="80"
            r="60"
            style={{
              stroke: color,
              strokeDasharray: circumference,
              strokeDashoffset: strokeDashoffset
            }}
          />
        </svg>
        <div className="gauge-center">
          <p className="font-mono text-2xl text-white">{value.toFixed(1)}</p>
          <p className="text-xs text-white/50">{unit}</p>
          <p className="text-xs text-white/40 mt-1">{label}</p>
        </div>
      </div>
    );
  };

  if (!connectionStatus.connected) {
    return (
      <div className="fade-in">
        <div className="header-bar">
          <div>
            <h1 className="header-title">Live Data Monitor</h1>
            <p className="header-subtitle">Real-time vehicle telemetry</p>
          </div>
        </div>
        
        <div className="bento-card text-center py-16">
          <Activity className="h-16 w-16 text-white/20 mx-auto mb-4" />
          <p className="text-white/50 text-lg">No vehicle connected</p>
          <p className="text-white/30 text-sm mt-2">Connect to your Tesla to view live data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="header-bar">
        <div>
          <h1 className="header-title">Live Data Monitor</h1>
          <p className="header-subtitle">Real-time vehicle telemetry</p>
        </div>
        
        <div className="flex items-center gap-4">
          {isStreaming && (
            <span className="flex items-center gap-2 text-[#30D158] font-mono text-sm">
              <span className="h-2 w-2 bg-[#30D158] rounded-full data-pulse"></span>
              STREAMING
            </span>
          )}
          <Button
            onClick={() => setIsStreaming(!isStreaming)}
            variant="outline"
            className="bg-transparent border-white/20 text-white hover:bg-white/5"
            data-testid="stream-toggle-btn"
          >
            {isStreaming ? (
              <>
                <Pause className="mr-2 h-4 w-4" />
                Pause
              </>
            ) : (
              <>
                <Play className="mr-2 h-4 w-4" />
                Resume
              </>
            )}
          </Button>
        </div>
      </div>

      {liveData && (
        <div className="bento-grid">
          {/* Battery Overview */}
          <div className="bento-card span-8">
            <div className="card-header">
              <h2 className="card-title">High Voltage Battery</h2>
              <span className="badge-success">Active</span>
            </div>
            
            <div className="grid grid-cols-4 gap-6">
              <div>
                <GaugeDisplay
                  value={liveData.battery_soc}
                  max={100}
                  unit="%"
                  label="State of Charge"
                  color="#30D158"
                />
              </div>
              <div>
                <GaugeDisplay
                  value={liveData.battery_voltage}
                  max={420}
                  unit="V"
                  label="Pack Voltage"
                  color="#007AFF"
                />
              </div>
              <div>
                <GaugeDisplay
                  value={Math.abs(liveData.battery_current)}
                  max={300}
                  unit="A"
                  label={liveData.battery_current > 0 ? "Discharging" : "Charging"}
                  color={liveData.battery_current > 0 ? "#FF9F0A" : "#30D158"}
                />
              </div>
              <div>
                <GaugeDisplay
                  value={liveData.battery_temp}
                  max={60}
                  unit="°C"
                  label="Pack Temperature"
                  color={liveData.battery_temp > 40 ? "#FF9F0A" : "#007AFF"}
                />
              </div>
            </div>
          </div>

          {/* 12V System */}
          <div className="bento-card span-4">
            <div className="card-header">
              <h2 className="card-title">12V Auxiliary System</h2>
              <span className="text-xs text-white/50 font-mono">33Ah - Frunk</span>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-white/60">Voltage</span>
                <span className={`font-mono text-xl ${liveData.aux_12v_voltage < 12.0 ? 'text-[#FF3B30]' : 'text-[#30D158]'}`}>
                  {liveData.aux_12v_voltage.toFixed(2)}V
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/60">Current Draw</span>
                <span className="font-mono text-xl text-white">
                  {liveData.aux_12v_current.toFixed(1)}A
                </span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${((liveData.aux_12v_voltage - 11) / 4) * 100}%` }}
                ></div>
              </div>
              {liveData.aux_12v_voltage < 12.0 && (
                <p className="text-xs text-[#FF3B30]">Low voltage warning - Check 12V battery</p>
              )}
            </div>
          </div>

          {/* Cell Voltage Grid */}
          <div className="bento-card span-6">
            <div className="card-header">
              <h2 className="card-title">Cell Voltage Map</h2>
              <span className="text-xs text-white/50 font-mono">96 Cells</span>
            </div>
            
            <div className="cell-grid" data-testid="cell-voltage-grid">
              {liveData.cell_voltages.slice(0, 96).map((voltage, index) => (
                <div
                  key={index}
                  className={`cell-item ${getCellColor(voltage)}`}
                  title={`Cell ${index + 1}: ${voltage.toFixed(3)}V`}
                ></div>
              ))}
            </div>
            
            <div className="flex justify-between mt-4 text-xs text-white/50">
              <span>Min: {Math.min(...liveData.cell_voltages).toFixed(3)}V</span>
              <span>Avg: {(liveData.cell_voltages.reduce((a, b) => a + b, 0) / liveData.cell_voltages.length).toFixed(3)}V</span>
              <span>Max: {Math.max(...liveData.cell_voltages).toFixed(3)}V</span>
            </div>
          </div>

          {/* Temperature Grid */}
          <div className="bento-card span-6">
            <div className="card-header">
              <h2 className="card-title">Thermal Management</h2>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 glass-card">
                <div className="flex items-center gap-2 mb-2">
                  <Thermometer className={`h-4 w-4 ${getTempColor(liveData.motor_front_temp)}`} />
                  <span className="text-xs text-white/50 uppercase">Front Motor</span>
                </div>
                <p className={`font-mono text-2xl ${getTempColor(liveData.motor_front_temp)}`}>
                  {liveData.motor_front_temp.toFixed(1)}°C
                </p>
              </div>
              
              <div className="p-4 glass-card">
                <div className="flex items-center gap-2 mb-2">
                  <Thermometer className={`h-4 w-4 ${getTempColor(liveData.motor_rear_temp)}`} />
                  <span className="text-xs text-white/50 uppercase">Rear Motor</span>
                </div>
                <p className={`font-mono text-2xl ${getTempColor(liveData.motor_rear_temp)}`}>
                  {liveData.motor_rear_temp.toFixed(1)}°C
                </p>
              </div>
              
              <div className="p-4 glass-card">
                <div className="flex items-center gap-2 mb-2">
                  <Thermometer className={`h-4 w-4 ${getTempColor(liveData.inverter_temp)}`} />
                  <span className="text-xs text-white/50 uppercase">Inverter</span>
                </div>
                <p className={`font-mono text-2xl ${getTempColor(liveData.inverter_temp)}`}>
                  {liveData.inverter_temp.toFixed(1)}°C
                </p>
              </div>
              
              <div className="p-4 glass-card">
                <div className="flex items-center gap-2 mb-2">
                  <Thermometer className={`h-4 w-4 ${getTempColor(liveData.coolant_temp)}`} />
                  <span className="text-xs text-white/50 uppercase">Coolant</span>
                </div>
                <p className={`font-mono text-2xl ${getTempColor(liveData.coolant_temp)}`}>
                  {liveData.coolant_temp.toFixed(1)}°C
                </p>
              </div>
            </div>
          </div>

          {/* Power Chart */}
          <div className="bento-card span-6">
            <div className="card-header">
              <h2 className="card-title">Power Output</h2>
              <span className="font-mono text-lg text-[#007AFF]">
                {liveData.power_output.toFixed(1)} kW
              </span>
            </div>
            
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={powerHistory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis dataKey="time" stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 10 }} />
                  <YAxis stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 10 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#121212', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '4px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="power" 
                    stroke="#007AFF" 
                    strokeWidth={2}
                    dot={false}
                    name="Power (kW)"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="regen" 
                    stroke="#30D158" 
                    strokeWidth={2}
                    dot={false}
                    name="Regen (kW)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Temperature Chart */}
          <div className="bento-card span-6">
            <div className="card-header">
              <h2 className="card-title">Temperature Trends</h2>
            </div>
            
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={tempHistory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis dataKey="time" stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 10 }} />
                  <YAxis stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 10 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#121212', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '4px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="battery" 
                    stroke="#007AFF" 
                    strokeWidth={2}
                    dot={false}
                    name="Battery (°C)"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="motor" 
                    stroke="#FF9F0A" 
                    strokeWidth={2}
                    dot={false}
                    name="Motor (°C)"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="inverter" 
                    stroke="#FF3B30" 
                    strokeWidth={2}
                    dot={false}
                    name="Inverter (°C)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Additional Stats */}
          <div className="bento-card span-12">
            <div className="card-header">
              <h2 className="card-title">System Status</h2>
            </div>
            
            <div className="grid grid-cols-6 gap-4">
              <div className="p-4 glass-card">
                <p className="text-xs text-white/50 uppercase mb-1">HVAC Power</p>
                <p className="font-mono text-xl text-white">{liveData.hvac_power.toFixed(1)} kW</p>
              </div>
              <div className="p-4 glass-card">
                <p className="text-xs text-white/50 uppercase mb-1">Regen Power</p>
                <p className="font-mono text-xl text-[#30D158]">{liveData.regen_power.toFixed(1)} kW</p>
              </div>
              <div className="p-4 glass-card">
                <p className="text-xs text-white/50 uppercase mb-1">Pack Voltage</p>
                <p className="font-mono text-xl text-white">{liveData.battery_voltage.toFixed(1)} V</p>
              </div>
              <div className="p-4 glass-card">
                <p className="text-xs text-white/50 uppercase mb-1">Pack Current</p>
                <p className="font-mono text-xl text-white">{liveData.battery_current.toFixed(1)} A</p>
              </div>
              <div className="p-4 glass-card">
                <p className="text-xs text-white/50 uppercase mb-1">Cell Imbalance</p>
                <p className="font-mono text-xl text-white">
                  {((Math.max(...liveData.cell_voltages) - Math.min(...liveData.cell_voltages)) * 1000).toFixed(0)} mV
                </p>
              </div>
              <div className="p-4 glass-card">
                <p className="text-xs text-white/50 uppercase mb-1">Timestamp</p>
                <p className="font-mono text-sm text-white/60">
                  {new Date(liveData.timestamp).toLocaleTimeString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveData;
